import { StateCreator } from 'zustand';
import { TQuoteStore } from '@store/types/GlobalStore.type';
import { PanelInstance, PanelSection, RuleEngine } from '@store/Quote/Quote.types';
import { GroupedQuoteItem } from '@lio/rule-engine/utils/quote-details';
import { sortBy } from 'lodash';
import { validateAvailabilityByBooleanOrRoles } from '@pages/QuoteDetails/helpers';
import { PanelInstanceSlice } from '@store/types/PanelInstanceSlice.type';
import { useStore } from '@store/GlobalStore';
import { getEngine } from '@lio/rule-engine';
import { getStructuredQuoteWithUnsavedValues } from '@store/Quote/quoteUnsavedValueOverlay';

const initialState = {
  validationErrorsById: {},
  quotePanelFieldsById: {},
  panelInstanceModeById: {},
  shownCardsByPanelId: {},
  panelInstanceStatusById: {},
  panelInstanceVisibilityById: {},
  quotePanelUnsavedFieldsById: {},
  ruleEngine: undefined,
  dirtyPanelInstanceIds: new Set<string>(),
  formValidationErrorsByFormId: {},
};

export const createPanelInstanceSlice: StateCreator<TQuoteStore, [], [], PanelInstanceSlice> = (set, get) => ({
  ...initialState,
  triggerValidation: () => {
    const panelInstances = get().panelInstances;
    const structuredQuote = get().structuredQuote;
    const storedAttributes = get().attributesById;
    const roles = useStore.getState()?.roles ?? [];

    const ruleEngine = get().ruleEngine;
    if (!ruleEngine || !structuredQuote) return;

    // Compute all validation errors and shown cards in one pass,
    // then apply them in a single store update (instead of N individual set() calls).
    //
    // FUTURE: ruleEngine.validateQuote() could replace the per-panel validateQuoteItem() loop
    // with a single call, then split results by rulePanelSectionId. The blocker is that
    // validateQuote() doesn't support limitToAttributeTypeIds, so we'd validate more than needed
    // and still filter per-panel for shown cards / statuses anyway.
    // The main perf win here is the single set() — the rule engine calls themselves are fast.
    const batchedValidationErrors: Record<string, any[]> = {};
    const batchedShownCards: Record<string, any[][]> = {};
    const structuredQuoteWithUnsavedValues = getStructuredQuoteWithUnsavedValues({
      storedAttributes,
      structuredQuote,
    });

    for (const panelInstance of panelInstances) {
      const { panelInstanceId } = panelInstance;
      const { quoteItem, panel } = panelInstance;
      if (!quoteItem) continue;

      const fieldsByPanelId = get().quotePanelFieldsById[panelInstanceId] ?? {};
      const fieldsInPanel = Object.values(fieldsByPanelId).flat();

      ruleEngine.updateContext(structuredQuoteWithUnsavedValues);
      ruleEngine.setEngineInitialQuoteItem(quoteItem.id);

      // --- Validation errors ---
      const panelSectionIds = panel.panelSections.map((ps: any) => ps.id);
      const validationErrors = ruleEngine
        .validateQuoteItem(quoteItem.id, {
          includeQuoteItemRules: true,
          limitToAttributeTypeIds: fieldsInPanel.map((f: any) => f.id),
        })
        .filter((ve: any) => {
          if (ve.ruleId || ve.rulePanelSectionId || ve.error.type === 'ValidationRuleFailed') {
            return panelSectionIds.includes(ve.rulePanelSectionId as string);
          }
          return true;
        });
      batchedValidationErrors[panelInstanceId] = validationErrors;

      // --- Shown cards ---
      const attributeTypesBySectionId = Object.values(quoteItem.attributeTypes).reduce<Record<string, any[]>>(
        (agg, at: any) => {
          if (at.panelSectionId) {
            agg[at.panelSectionId] = agg[at.panelSectionId] ?? [];
            agg[at.panelSectionId].push(at);
          }
          return agg;
        },
        {},
      );

      const cards: PanelSection[][] = [];
      sortBy(panel.panelSections, ['cardIndex', 'order']).forEach((ps: any) => {
        if (!cards[ps.cardIndex - 1]) {
          cards[ps.cardIndex - 1] = [];
        }
        cards[ps.cardIndex - 1].push(ps);
      });

      const cardsWithFieldGroups = cards.map((cardSections) => {
        const fieldGroups = cardSections.filter((section) => section.groupIndex !== null);
        if (fieldGroups.length > 0) {
          const parentSections = cardSections.filter((section) => section.groupIndex === null);
          return parentSections.map((ps) => {
            const childFieldGroups = fieldGroups
              .filter((fg) => fg.order === ps.order && fg.cardIndex === ps.cardIndex)
              .sort((a, b) => ((a.groupIndex ?? 0) < (b.groupIndex ?? 0) ? -1 : 1));
            return { ...ps, fieldGroups: childFieldGroups ?? [] };
          });
        }
        return cardSections.map((t) => ({ ...t, fieldGroups: [] }));
      });

      const shownCards = cardsWithFieldGroups
        .map((cardSections) => {
          const shownSections = getShownSections(cardSections as any, attributeTypesBySectionId, ruleEngine!, roles);
          return shownSections.length > 0 ? shownSections : [];
        })
        .filter((c) => c.length > 0);

      batchedShownCards[panelInstanceId] = shownCards;
    }

    // Single store update for all panels
    set((prev) => ({
      ...prev,
      validationErrorsById: { ...prev.validationErrorsById, ...batchedValidationErrors },
      shownCardsByPanelId: { ...prev.shownCardsByPanelId, ...batchedShownCards },
      ruleEngine,
    }));

    get().triggerFormValidation();

    // leaving commented out for use of debugging later
    // console.log(
    //   `[triggerValidation] ${panelInstances.length} panels — compute: ${(tBeforeSet - t0).toFixed(0)}ms, store set: ${(tAfterSet - tBeforeSet).toFixed(0)}ms, total: ${(tAfterSet - t0).toFixed(0)}ms`,
    // );
  },
  triggerValidationByPanel: ({ panelInstanceId, ruleEngine: panelRuleEngine }) => {
    // EC: this is the weirdest part of the rule engine moving.. but basically we need the rule engine to trigger shown cards at the end
    // this 1) updates the visibility correctly but 2) is needed to update the fields to their newly saved attribute value
    const structuredQuote = get().structuredQuote;

    const panelInstance = get().panelInstances.find((p) => p.panelInstanceId === panelInstanceId);

    const { quoteItem } = panelInstance ?? {};
    const quoteItemId = quoteItem?.id;

    const storedAttributes = get().attributesById;

    const fieldsByPanelId = get().quotePanelFieldsById[panelInstanceId] ?? {};
    const fieldsInPanel = Object.values(fieldsByPanelId).flat();

    const structuredQuoteWithUnsavedValues = quoteItemId
      ? getStructuredQuoteWithUnsavedValues({
          storedAttributes,
          structuredQuote,
        })
      : structuredQuote;

    let ruleEngine = panelRuleEngine || get().ruleEngine;
    if (!ruleEngine) {
      ruleEngine = getEngine(structuredQuoteWithUnsavedValues) as any;
      set((prev) => ({ ...prev, ruleEngine }));
    } else {
      // Update context only once per batch; context gets unsaved overlay if needed
      ruleEngine.updateContext(structuredQuoteWithUnsavedValues);
    }
    if (!ruleEngine) {
      return; // cannot validate without engine
    }
    if (quoteItem) ruleEngine.setEngineInitialQuoteItem(quoteItem.id);

    set((prev) => {
      if (!structuredQuote || !panelInstance || !quoteItemId) {
        return prev;
      }

      const panelSectionIds = panelInstance.panel.panelSections.map((ps) => ps.id);

      const validationErrors = ruleEngine
        .validateQuoteItem(quoteItemId, {
          includeQuoteItemRules: true,
          limitToAttributeTypeIds: fieldsInPanel.map((f) => f.id),
        })
        .filter((ve) => {
          if (ve.ruleId || ve.rulePanelSectionId || ve.error.type === 'ValidationRuleFailed') {
            return panelSectionIds.includes(ve.rulePanelSectionId as string);
          }
          return true;
        });

      return {
        ...prev,
        validationErrorsById: {
          ...prev.validationErrorsById,
          [panelInstanceId]: validationErrors,
        },
        ruleEngine,
      };
    });

    if (ruleEngine) {
      get().setShownCardsByPanelId({ panelInstanceId, ruleEngine: ruleEngine as any });
    }
  },
  setShownCardsByPanelId: ({ panelInstanceId, ruleEngine }) => {
    set((prev) => {
      const panelInstance = get().panelInstances.find((p) => p.panelInstanceId === panelInstanceId);

      if (!panelInstance) {
        return prev;
      }

      const { quoteItem, panel } = panelInstance;
      const roles = useStore.getState().roles;

      const attributeTypesBySectionId = Object.values(quoteItem.attributeTypes).reduce<
        Record<string, (typeof quoteItem)['attributeTypes'][string][]>
      >((agg, at) => {
        if (at.panelSectionId) {
          agg[at.panelSectionId] = agg[at.panelSectionId] ?? [];
          agg[at.panelSectionId].push(at);
        }
        return agg;
      }, {});

      const cards: PanelSection[][] = [];
      sortBy(panel.panelSections, ['cardIndex', 'order']).forEach((ps) => {
        if (!cards[ps.cardIndex - 1]) {
          cards[ps.cardIndex - 1] = [];
        }
        cards[ps.cardIndex - 1].push(ps);
      });

      const cardsWithFieldGroups = cards.map((cardSections) => {
        const fieldGroups = cardSections.filter((section) => {
          return section.groupIndex !== null;
        });

        if (fieldGroups.length > 0) {
          const parentSections = cardSections.filter((section) => {
            return section.groupIndex === null;
          });

          const sectionsWithFieldGroups = parentSections.map((ps) => {
            const childFieldGroups = fieldGroups
              .filter((fg) => fg.order === ps.order && fg.cardIndex === ps.cardIndex)
              .sort((a, b) => {
                if (!a.groupIndex || !b.groupIndex) {
                  return 0;
                }
                if (a.groupIndex < b.groupIndex) {
                  return -1;
                }
                return 1;
              });

            if (childFieldGroups) {
              return {
                ...ps,
                fieldGroups: childFieldGroups,
              };
            }
            return {
              ...ps,
              fieldGroups: [],
            };
          });

          return sectionsWithFieldGroups;
        }

        return cardSections?.map((t) => ({ ...t, fieldGroups: [] }));
      });

      const shownCards = cardsWithFieldGroups
        .map((cardSections) => {
          const shownSections = getShownSections(cardSections, attributeTypesBySectionId, ruleEngine, roles);

          return shownSections.length > 0 ? shownSections : [];
        })
        ?.filter((c) => c.length > 0);

      return {
        ...prev,
        shownCardsByPanelId: {
          ...prev.shownCardsByPanelId,
          [panelInstanceId]: shownCards,
        },
      };
    });
  },
  triggerFormValidation: () => {
    const ruleEngine = get().ruleEngine;
    if (!ruleEngine) return;

    const structuredQuote = get().structuredQuote as any;
    if (!structuredQuote?.quoteFormsV2) return;

    const unsavedFormAttributes = get().unsavedFormAttributes;
    const hasUnsaved = Object.keys(unsavedFormAttributes).length > 0;

    // Merge unsaved form attribute values into quoteFormsV2 for real-time validation
    let mergedQuoteFormsV2 = structuredQuote.quoteFormsV2;
    if (hasUnsaved) {
      mergedQuoteFormsV2 = structuredQuote.quoteFormsV2.map((qf: any) => {
        const matchedKeys = new Set<string>();

        const updatedAttributes = qf.quoteFormAttributes.map((qfa: any) => {
          const key = `${qf.id}-${qfa.formAttributeTypeId}-${qfa.listItemGroupIndex ?? 0}`;
          const unsaved = unsavedFormAttributes[key];
          if (unsaved) {
            matchedKeys.add(key);
            return {
              ...qfa,
              value: unsaved.value,
              ...(unsaved.valueOverride !== undefined ? { valueOverride: unsaved.valueOverride } : {}),
            };
          }
          return qfa;
        });

        // Append new unsaved attributes for this form (e.g. new repeatable group entries).
        // Look up formAttributeType from an existing record so the engine context stays valid.
        const newAttributes = Object.entries(unsavedFormAttributes)
          .filter(([key, attr]) => attr.quoteFormId === qf.id && !matchedKeys.has(key))
          .map(([, attr]) => {
            const formAttributeType =
              qf.formVersion?.formAttributeTypes?.find((fat: any) => fat.id === attr.formAttributeTypeId) ??
              qf.quoteFormAttributes.find((qfa: any) => qfa.formAttributeTypeId === attr.formAttributeTypeId)
                ?.formAttributeType ??
              null;
            return {
              id: null,
              formAttributeTypeId: attr.formAttributeTypeId,
              listItemGroupIndex: attr.listItemGroupIndex ?? null,
              value: attr.value,
              valueOverride: attr.valueOverride ?? null,
              formAttributeType,
            };
          })
          .filter((attr: any) => attr.formAttributeType != null);

        return {
          ...qf,
          quoteFormAttributes: [...updatedAttributes, ...newAttributes],
        };
      });
    }

    const structuredQuoteWithUnsavedValues = getStructuredQuoteWithUnsavedValues({
      structuredQuote,
      storedAttributes: get().attributesById,
    });

    // Update engine context with both quote-item drafts and merged form data, then validate each form
    ruleEngine.updateContext({ ...structuredQuoteWithUnsavedValues, quoteFormsV2: mergedQuoteFormsV2 });

    const errorsByFormId: Record<string, any[]> = {};
    for (const qf of mergedQuoteFormsV2) {
      try {
        const formErrors = (ruleEngine as any).validateQuoteForm?.(qf.id) ?? [];
        errorsByFormId[qf.id] = formErrors;
      } catch {
        errorsByFormId[qf.id] = [];
      }
    }

    set((prev) => ({
      ...prev,
      formValidationErrorsByFormId: errorsByFormId,
    }));
  },
  updatePanelStatus: (panelInstanceId, newStatus) =>
    set((prev) => ({
      ...prev,
      panelInstanceStatusById: {
        ...prev.panelInstanceStatusById,
        [panelInstanceId]: newStatus,
      },
    })),
  updatePanelVisibility: (panelInstanceId, visibility) =>
    set((prev) => ({
      ...prev,
      panelInstanceVisibilityById: {
        ...prev.panelInstanceVisibilityById,
        [panelInstanceId]: visibility,
      },
    })),
  updatePanelMode: (panelInstanceId, newMode) =>
    set((prev) => ({
      ...prev,
      panelInstanceModeById: {
        ...prev.panelInstanceModeById,
        [panelInstanceId]: newMode,
      },
    })),
});

const getShownFields = ({
  fields,
  ruleEngine,
  roles,
}: {
  fields: GroupedQuoteItem['attributeTypes'][string][];
  ruleEngine: RuleEngine;
  roles: string[];
}) => {
  return (
    fields?.filter((f) => {
      try {
        const isActive = ruleEngine.execute(f.isActive);

        if (!isActive) {
          return false;
        }
        const isVisible = validateAvailabilityByBooleanOrRoles(
          ruleEngine.execute(f.isVisible) as boolean | string[],
          roles,
        );

        return isVisible;
      } catch (e) {
        console.error(e);
        return false;
      }
    }) ?? []
  );
};

const getShownFieldGroups = (
  fieldGroups: PanelInstance['panel']['panelSections'],
  attributeTypesBySectionId: Record<string, GroupedQuoteItem['attributeTypes'][string][]>,
  ruleEngine: RuleEngine,
  roles: string[],
) => {
  return fieldGroups
    .map((fg) => {
      const fields = attributeTypesBySectionId[fg.id];

      const shownFields = getShownFields({ fields, ruleEngine, roles });

      return { ...fg, shownFields };
    })
    .filter((fg) => fg.shownFields.length > 0);
};

interface PanelWithFieldGroup extends PanelSection {
  fieldGroups: PanelSection[];
}

const getShownSections = (
  sections: PanelWithFieldGroup[],
  attributeTypesBySectionId: Record<string, GroupedQuoteItem['attributeTypes'][string][]>,
  ruleEngine: RuleEngine,
  roles: string[],
) => {
  return sections.flatMap((section) => {
    const fields = attributeTypesBySectionId[section.id];
    const shownFields = getShownFields({ fields, ruleEngine, roles });

    if (section.fieldGroups) {
      const shownFieldGroups = getShownFieldGroups(section.fieldGroups, attributeTypesBySectionId, ruleEngine, roles);

      if (shownFieldGroups.length > 0) {
        return [{ ...section, shownFields, fieldGroups: shownFieldGroups }];
      }
    }

    if (shownFields.length > 0) {
      return [{ ...section, shownFields, fieldGroups: [] }];
    }

    return [];
  });
};
